/**
 * Shared utilities for {@link PairRow}, {@link PairCard}, {@link PairsDataTable}, {@link renderPairDetails}
 * (row/list helpers, no JSX).
 */

import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { LucideIcon } from 'lucide-react';
import {
  DEFAULT_PAIR_INPERSON_FILTERS as DEFAULT_FILTERS,
  type PairInPersonFilters as Filters,
  PAIR_TYPE_CONFIG,
  type PairTypeConfig,
} from '@/components/pairs/pair-configs';
import type { SessionStudentTutorPair } from '@/types/inperson/pair.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import { getPrimaryRole } from '@/utils/primary-role-utils';

export { getPairExportHeaders } from '@/utils/common-utils';

/** RBAC actions available on an in-person pair row. */
export interface PageActions {
  canViewRow?: boolean;
  canDeleteRow?: boolean;
}

/** Row actions plus CSV export for the in-person pairs table. */
export interface DataTablePageActions extends PageActions {
  canExportCsv?: boolean;
}

/** Date or ISO string accepted by pair formatters. */
type PairDateInput = Date | string | undefined;

/** Derived fields for {@link PairRow} / {@link PairCard} / the detail collapse. */
export interface Display {
  typeConfig: PairTypeConfig;
  TypeIcon: LucideIcon;
  sessionDate: string;
  sessionTimeRange: string | null;
  establishmentName: string;
  studentName: string;
  tutorName: string;
  tutorRoleLabel: string | undefined;
  studentLevel: string | undefined;
  subjects: string[];
  subjectCount: number;
  subjectCountLabel: string;
}

/** Invalid or empty dates become null. */
export function parsePairDate(date: PairDateInput): Date | null {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

/** First 5 chars (`HH:mm`); `empty` when missing. */
export function formatPairTime(time: string | undefined | null, empty = ''): string {
  if (!time) return empty;
  return time.slice(0, 5);
}

/** `start - end` using {@link formatPairTime}. */
export function formatPairTimeRange(
  start?: string | null,
  end?: string | null,
  empty = '',
  separator = ' - ',
): string {
  return `${formatPairTime(start, empty)}${separator}${formatPairTime(end, empty)}`;
}

/** `date-fns` format with {@link fr} locale. */
export function formatPairDate(date: PairDateInput, pattern: string, empty = '-'): string {
  const d = parsePairDate(date);
  if (!d) return empty;
  return format(d, pattern, { locale: fr });
}

/** Weekday + long month in `fr-FR`. */
export function formatPairLongDate(date: PairDateInput): string {
  const d = parsePairDate(date);
  if (!d) return '-';
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

/** Numeric `dd/mm/yyyy` in `fr-FR`. */
export function formatPairShortDate(date: PairDateInput): string {
  const d = parsePairDate(date);
  if (!d) return '-';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** Created-at as a short `fr-FR` date. */
export function formatPairCreatedAt(createdAt: string | Date): string {
  return new Date(createdAt).toLocaleDateString('fr-FR');
}

/** Student display name; dash when missing. */
export function getPairStudentDisplayName(pair: SessionStudentTutorPair): string {
  return pair.studentUser?.displayName || '-';
}

/** Tutor first+last; dash when both empty. */
export function getPairTutorDisplayName(pair: SessionStudentTutorPair): string {
  return `${pair.tutorUser?.firstName || ''} ${pair.tutorUser?.lastName || ''}`.trim() || '-';
}

/** Pluralized subjects-count badge. */
export function formatPairSubjectCountLabel(count: number): string {
  return `${count} matière${count > 1 ? 's' : ''}`;
}

/** Comma-joined subjects; fallback when the list is empty. */
export function formatPairSubjectsTooltip(subjects: string[]): string {
  return subjects.length > 0 ? subjects.join(', ') : 'Aucune matière';
}

/** Levels from every student profile. */
export function getPairStudentLevels(
  pair: SessionStudentTutorPair,
): Array<{ id: string; label: string }> {
  return (pair.studentUser?.userProfiles ?? [])
    .map((profile) => profile.level)
    .filter((level): level is { id: string; label: string } => level != null);
}

/** Subject names from every student profile. */
export function getPairStudentSubjectNames(pair: SessionStudentTutorPair): string[] {
  return (pair.studentUser?.userProfiles ?? []).flatMap((profile) => profile.subjectNames ?? []);
}

/** Taught subjects from tutor profiles (`id` + `label` only). */
export function getPairTutorTaughtSubjects(
  pair: SessionStudentTutorPair,
): Array<{ id: string; label: string }> {
  return (pair.tutorUser?.userProfiles ?? []).flatMap((profile) => {
    const subjects = profile.subjects;
    if (!Array.isArray(subjects)) return [];
    return subjects.filter((subject): subject is { id: string; label: string } => {
      if (subject == null || typeof subject !== 'object') return false;
      return 'id' in subject && 'label' in subject;
    });
  });
}

/** Type badge, session, names, level, and subjects for one pair. */
export function deriveDisplay(pair: SessionStudentTutorPair): Display {
  const typeConfig = PAIR_TYPE_CONFIG[pair.assignmentSource] || PAIR_TYPE_CONFIG.manual;
  const studentProfile = pair.studentUser?.userProfiles?.[0];
  const subjects = studentProfile?.subjectNames || [];
  const subjectCount = subjects.length;

  return {
    typeConfig,
    TypeIcon: typeConfig.icon,
    sessionDate: pair.session?.sessionDate
      ? formatPairDate(pair.session.sessionDate, 'EEEE d MMMM yyyy')
      : '-',
    sessionTimeRange: pair.session?.startTime
      ? formatPairTimeRange(pair.session.startTime, pair.session.endTime, '--:--')
      : null,
    establishmentName: pair.session?.establishment?.name || 'Non défini',
    studentName: getPairStudentDisplayName(pair),
    tutorName: getPairTutorDisplayName(pair),
    tutorRoleLabel: getPrimaryRole(pair.tutorUser?.userRoles)?.label,
    studentLevel: studentProfile?.level?.label,
    subjects,
    subjectCount,
    subjectCountLabel: formatPairSubjectCountLabel(subjectCount),
  };
}

/** Custom dates win; today/week/month presets otherwise. */
export function resolveDateRange(filters: Filters): { dateFrom?: Date; dateTo?: Date } {
  return resolveListDateRange(filters, { periodEnd: 'endOfPeriod' });
}

/** Map UI filters + pagination to in-person pairs list params. */
export function buildInPersonPairsListParams(
  filters: Filters,
  options: { page: number; pageSize: number },
) {
  const { dateFrom, dateTo } = resolveDateRange(filters);
  return {
    page: options.page,
    pageSize: options.pageSize,
    types: filters.types.length > 0 ? filters.types : undefined,
    establishmentId: filters.establishmentId,
    subjectId: filters.subjectId,
    tutorSearch: filters.tutorSearch,
    studentSearch: filters.studentSearch,
    dateFrom,
    dateTo,
  };
}

/** Any type, date, site, subject, or name search besides the default. */
export function hasActiveFilters(filters: Filters): boolean {
  return (
    filters.types.length > 0 ||
    filters.dateRange !== DEFAULT_FILTERS.dateRange ||
    !!filters.customDateFrom ||
    !!filters.customDateTo ||
    !!filters.establishmentId ||
    !!filters.subjectId ||
    !!filters.tutorSearch ||
    !!filters.studentSearch
  );
}

/** Allowed by RBAC and currently visible in user preferences. */
export function isPairFilterVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Add the value if missing, remove it if present. */
export function toggleListValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/** Local `YYYY-MM-DD` (not UTC) so calendar filters stay on the picked day. */
export function formatPairFilterDateToString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Local calendar date from an `<input type="date">`; `endOfDay` sets 23:59:59. */
export function parsePairFilterDateInput(val: string, endOfDay: boolean): Date {
  const [year, month, day] = val.split('-').map(Number);
  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59)
    : new Date(year, month - 1, day, 0, 0, 0);
}

/** One CSV row; tutor displayName wins over first+last. */
export function buildExportRow(pair: SessionStudentTutorPair): string[] {
  const tutorDisplay = pair.tutorUser?.displayName?.trim() || getPairTutorDisplayName(pair);
  const sessionDate = formatPairShortDate(pair.session?.sessionDate);
  return [
    pair.studentUser?.displayName || '-',
    pair.studentUser?.email ?? '',
    tutorDisplay,
    pair.tutorUser?.email ?? '',
    PAIR_TYPE_CONFIG[pair.assignmentSource]?.label || pair.assignmentSource,
    `Séance ${sessionDate}`,
    sessionDate,
    formatPairTimeRange(pair.session?.startTime, pair.session?.endTime),
    pair.session?.establishment?.name || '-',
    pair.session?.subject?.label || '-',
  ];
}

/** Delete-confirm label: student ↔ tutor (fallbacks when a name is missing). */
export function getPairDeleteItemName(pair: SessionStudentTutorPair): string {
  const studentName = pair.studentUser?.displayName || getRoleLabel('student');
  const tutorName = getPairTutorDisplayName(pair);
  return `${studentName} ↔ ${tutorName === '-' ? getRoleLabel('tutor') : tutorName}`;
}

/** Central error copy from {@link getErrorMessage}. */
export function getApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
